// src/components/common/RouteErrorBoundary.jsx
import React from 'react';
import { useRouteError, useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, ArrowPathIcon, HomeIcon } from '@heroicons/react/24/outline';

export default function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  const errorMessage = error?.statusText || error?.message || 'An unexpected rendering error occurred.';
  const errorStack = error?.stack || '';

  const handleGoHome = () => {
    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;
      if (user?.role === 'Customer') {
        navigate('/admin/customer-portal');
      } else {
        navigate('/admin/dashboard');
      }
    } catch {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F4EE] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
        <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-5 shadow-inner">
          <ExclamationTriangleIcon className="w-8 h-8" />
        </div>

        <h1 className="text-xl font-bold text-gray-900 mb-2">
          Unable to Load Screen
        </h1>
        <p className="text-sm text-gray-600 mb-6 leading-relaxed">
          {errorMessage}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
          <button
            onClick={() => window.location.reload()}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1B2A41] text-white rounded-xl text-sm font-semibold hover:bg-[#243B5A] transition shadow-sm"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Reload Page
          </button>
          <button
            onClick={handleGoHome}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#E8612C] text-white rounded-xl text-sm font-semibold hover:bg-[#D55320] transition shadow-sm"
          >
            <HomeIcon className="w-4 h-4" />
            Dashboard
          </button>
        </div>

        {process.env.NODE_ENV !== 'production' && errorStack && (
          <details className="text-left mt-4 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-200 overflow-auto max-h-40 font-mono">
            <summary className="cursor-pointer font-bold text-gray-700 mb-1">
              Developer Stack Trace
            </summary>
            <pre className="whitespace-pre-wrap">{errorStack}</pre>
          </details>
        )}
      </div>
    </div>
  );
}
